import { createPermissions, hasRole } from 'cesg-shared';
import { UserRole, type SessionUser } from 'cesg-shared/dist/models';
import { computed } from 'vue';

type ButtonState = 'visible' | 'hidden';
type InputState = 'edit' | 'read-only' | 'hidden';

export interface LiftPlanPermissions {
  components: {
    deleteEntry: ButtonState;
    deleteSubtotal: ButtonState;
    addLift: ButtonState;
    addRow: ButtonState;
    contractSum: InputState;
    retentionPercentage: InputState;
    sectionCard: {
      edit: ButtonState;
      title: InputState;
    };
    retentionAmount: InputState;
    entreprenadsumma: InputState;
  };
  columns: Record<string, InputState>;
  subtotalColumns: Record<string, InputState>;
}

interface UseLiftPlanPermissionsProps {
  isEditing: () => boolean;
  projectId: string;
  user: SessionUser;
}

export const useLiftPlanPermissions = ({
  isEditing,
  user,
  projectId,
}: UseLiftPlanPermissionsProps) => {
  return computed<LiftPlanPermissions>(() => {
    const buttonState: ButtonState = isEditing() ? 'visible' : 'hidden';
    const inputState: InputState = isEditing() ? 'edit' : 'read-only';
    const permissions = createPermissions(projectId, user);

    return {
      components: {
        deleteEntry: buttonState,
        deleteSubtotal: buttonState,
        addLift: buttonState,
        addRow: buttonState,
        name: 'read-only',
        contractSum: 'read-only',
        retentionPercentage: inputState,
        retentionAmount: 'read-only',
        entreprenadsumma: 'hidden',
        sectionCard: {
          edit:
            permissions.edit.overview && hasRole(UserRole.CESG, user)
              ? 'visible'
              : 'hidden',
          title: 'read-only',
        },
      },
      columns: {
        number: 'read-only',
        description: inputState,
        dueDate: inputState,
        amount: inputState,
        retention: inputState,
        amountDue: inputState,
        invoicedDate: hasRole(UserRole.ADMIN, user) ? inputState : 'read-only',
        paymentDate: hasRole(UserRole.ADMIN, user) ? inputState : 'read-only',
        comment: hasRole(UserRole.ADMIN, user) ? inputState : 'read-only',
      },
      subtotalColumns: {
        label: inputState,
        amount: inputState,
        invoicedDate: hasRole(UserRole.ADMIN, user) ? inputState : 'read-only',
        paymentDate: hasRole(UserRole.ADMIN, user) ? inputState : 'read-only',
        comment: hasRole(UserRole.ADMIN, user) ? inputState : 'read-only',
      },
    };
  });
};
